import { Head, Link, usePage } from "@inertiajs/react"
import { BedDouble, ExternalLink, Image, MapPin } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { StatusChip } from "@/components/ui/status-chip"
import type { PageProps } from "@/types/inertia"

type SiteNode = {
  id: number
  name: string
  slug: string
  status: string
  rooms_count: number
  photo_count: number
}

// visible: null = section has no on/off toggle; false = hidden from the
// live page (red dot in the breadcrumb).
type SectionNode = { label: string; visible: boolean | null }

type PageNode = {
  key: string
  label: string
  path: string
  public_url: string
  draft: boolean
  // false when the whole page is switched off (PageVisibility) — shown as a
  // passive red-dot "Off" marker; the interactive switch lives on the page's
  // edit screen and the Features dashboard, not inside this stretched-link card.
  visible: boolean
  sections: SectionNode[]
  href: string | null
  site?: SiteNode
  gallery_links?: { site_id: number; site_name: string; href: string }[]
  profile_link?: { label: string; href: string }
  settings_link?: { label: string; href: string }
}

type BrandGroup = {
  key: "gisela" | "sanctuary"
  name: string
  host: string
  pages: PageNode[]
}

type Props = PageProps & {
  brands: BrandGroup[]
  brand_filter: string | null
}

function SiteMeta({ site }: { site: SiteNode }) {
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
      <span className="inline-flex items-center gap-1">
        <MapPin className="h-3.5 w-3.5" /> Location
      </span>
      <span className="inline-flex items-center gap-1">
        <BedDouble className="h-3.5 w-3.5" /> {site.rooms_count}{" "}
        {site.rooms_count === 1 ? "room" : "rooms"}
      </span>
      <span className="inline-flex items-center gap-1">
        <Image className="h-3.5 w-3.5" /> {site.photo_count}{" "}
        {site.photo_count === 1 ? "photo" : "photos"}
      </span>
      <StatusChip status={site.status === "active" ? "active" : "inactive"} />
    </span>
  )
}

function ViewLive({ path, url }: { path: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener"
      className="relative z-10 inline-flex items-center gap-1 text-xs text-ink-muted no-underline transition-colors hover:text-ink-display"
    >
      {path}
      <ExternalLink className="h-3 w-3" />
    </a>
  )
}

// Cards use the stretched-link pattern: the title Link's ::after covers the
// whole card, and inner links (view live, gallery, profile) sit above it on
// z-10 — no nested anchors.
const CARD_CLASS =
  "relative rounded-lg border border-hairline bg-surface p-4 transition-colors has-[a.stretched:hover]:border-accent"

function CardTitle({ label, href }: { label: string; href: string | null }) {
  if (!href) return <span className="font-medium text-ink-display">{label}</span>
  return (
    <Link
      href={href}
      className="stretched font-medium text-ink-display no-underline after:absolute after:inset-0"
    >
      {label}
    </Link>
  )
}

function NodeCard({ node }: { node: PageNode }) {
  return (
    <div className={CARD_CLASS}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <CardTitle label={node.label} href={node.href} />
        {node.draft && <StatusChip status="draft" />}
        {!node.visible && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-danger-display">
            <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-danger" />
            Off
          </span>
        )}
        <ViewLive path={node.path} url={node.public_url} />
      </div>
      {node.site && (
        <div className="mt-1.5">
          <SiteMeta site={node.site} />
        </div>
      )}
      {node.sections.length > 0 && (
        <p className="mt-1.5 text-xs text-ink-muted">
          {node.sections.map((section, i) => (
            <span key={i}>
              {i > 0 && " · "}
              <span className={section.visible === false ? "opacity-60" : undefined}>
                {section.visible === false && (
                  <span
                    aria-hidden
                    className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-danger align-middle"
                  />
                )}
                {section.label}
              </span>
            </span>
          ))}
        </p>
      )}
      {node.profile_link && (
        <div className="mt-1.5">
          <Link href={node.profile_link.href} className="relative z-10 text-xs">
            {node.profile_link.label} →
          </Link>
        </div>
      )}
      {node.settings_link && (
        <div className="mt-1.5">
          <Link href={node.settings_link.href} className="relative z-10 text-xs">
            {node.settings_link.label} →
          </Link>
        </div>
      )}
      {node.gallery_links && (
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
          {node.gallery_links.map((g) => (
            <Link key={g.site_id} href={g.href} className="relative z-10 text-xs">
              {g.site_name} gallery →
            </Link>
          ))}
          {node.gallery_links.length === 0 && (
            <p className="text-xs text-ink-muted">No active locations yet.</p>
          )}
        </div>
      )}
    </div>
  )
}

export default function WebsiteIndex() {
  const { props } = usePage<Props>()
  const { brands, brand_filter } = props
  const flash = props.flash

  const title = brand_filter
    ? `Website Pages — ${brands[0]?.name ?? ""}`
    : "Website Pages"

  return (
    <AdminShell>
      <Head title={title}>
        <meta
          name="description"
          content="The public sites' structure, page by page — edit any page or location from here."
        />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content="The public sites' structure, page by page — edit any page or location from here."
        />
      </Head>

      <PageHeader
        title={title}
        description="Everything shown on the public sites, page by page. Open a page to edit its content."
        actions={
          <div className="flex flex-wrap gap-2">
            {(!brand_filter || brand_filter === "gisela") && (
              <>
                {/* Listed-only and link entries have no page, so no node in
                    the tree below — the Condition menu screen is their home. */}
                <Button variant="ghost" asChild>
                  <Link href="/admin/website/condition-categories">Condition menu</Link>
                </Button>
                <Button variant="secondary" asChild>
                  <Link href="/admin/website/conditions/new">+ New condition page</Link>
                </Button>
                <Button variant="secondary" asChild>
                  <Link href="/admin/website/therapies/new">+ New therapy page</Link>
                </Button>
              </>
            )}
            {(!brand_filter || brand_filter === "sanctuary") && (
              <Button asChild>
                <Link href="/admin/website/locations/new">+ Add location</Link>
              </Button>
            )}
          </div>
        }
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {flash?.alert && <p className="mt-4 text-sm text-danger-display">{flash.alert}</p>}

      {brands.map((brand) => (
        <div key={brand.key} className="mt-8 max-w-3xl">
          {!brand_filter && (
            <div className="mb-3 flex flex-wrap items-baseline gap-x-3">
              <h2>{brand.name}</h2>
              <span className="text-xs text-ink-muted">{brand.host}</span>
            </div>
          )}
          <div className="flex flex-col gap-3">
            {brand.pages.map((node) => (
              <NodeCard key={node.key} node={node} />
            ))}
          </div>

        </div>
      ))}
    </AdminShell>
  )
}
